import React, { useState } from "react";
import { Edit3, Trash, Eye } from "lucide-react"; // Icons for actions
import AddInventoryModal from "../components/AddInventoryModal";
import EditInventoryModal from "../components/EditInventoryModal";
import InventoryDetailsModal from "../components/InventoryDetailsModal";

const initialInventory = [
  {
    id: 1,
    name: "Laptop",
    sku: "LAP123",
    category: "Electronics",
    quantity: 50,
    reorderLevel: 10,
    unitPrice: 1000,
    supplier: "TechSupplier Inc.",
    status: "In Stock",
  },
  {
    id: 2,
    name: "Office Chair",
    sku: "CHAIR456",
    category: "Furniture",
    quantity: 5,
    reorderLevel: 10,
    unitPrice: 150,
    supplier: "FurniCo",
    status: "Low Stock",
  },
  {
    id: 3,
    name: "Printer Ink",
    sku: "INK789",
    category: "Office Supplies",
    quantity: 0,
    reorderLevel: 5,
    unitPrice: 30,
    supplier: "PrintWorld",
    status: "Out of Stock",
  },
];

function InventoryDashboard() {
  const [inventory, setInventory] = useState(initialInventory);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Filtered inventory based on search, category, and status
  const filteredInventory = inventory
    .filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
    .filter((item) => (selectedCategory ? item.category === selectedCategory : true))
    .filter((item) => (selectedStatus ? item.status === selectedStatus : true));

  const handleAddItem = (newItem) => {
    setInventory([
      ...inventory,
      {
        ...newItem,
        id: inventory.length + 1,
        status:
          newItem.quantity === 0
            ? "Out of Stock"
            : newItem.quantity <= newItem.reorderLevel
            ? "Low Stock"
            : "In Stock",
      },
    ]);
    setIsAdding(false);
  };

  const handleEditItem = (updatedItem) => {
    setInventory(
      inventory.map((item) =>
        item.id === updatedItem.id
          ? {
              ...updatedItem,
              status:
                updatedItem.quantity === 0
                  ? "Out of Stock"
                  : updatedItem.quantity <= updatedItem.reorderLevel
                  ? "Low Stock"
                  : "In Stock",
            }
          : item
      )
    );
    setEditingItem(null);
  };

  const handleDeleteItem = (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      setInventory(inventory.filter((item) => item.id !== id));
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
<div className="flex flex-col md:flex-row justify-between items-center mb-4">
  <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-2 md:mb-0">
    Inventory Management
  </h2>
  <button
    onClick={() => setIsAdding(true)}
    className="bg-blue-600 text-white px-3 py-2 text-sm rounded-md hover:bg-blue-500"
  >
    + Add Item
  </button>
</div>
      {/* Add, Edit, and Details Modals */}
      {isAdding && (
        <AddInventoryModal onClose={() => setIsAdding(false)} onSave={handleAddItem} />
      )}
      {editingItem && (
        <EditInventoryModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={handleEditItem}
        />
      )}
      {selectedItem && (
        <InventoryDetailsModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <input
          type="text"
          placeholder="Search inventory..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md p-2 border rounded shadow-sm"
        />
        <select
          className="p-2 border rounded shadow-sm"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          <option value="Electronics">Electronics</option>
          <option value="Furniture">Furniture</option>
          <option value="Office Supplies">Office Supplies</option>
        </select>
        <select
          className="p-2 border rounded shadow-sm"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="In Stock">In Stock</option>
          <option value="Low Stock">Low Stock</option>
          <option value="Out of Stock">Out of Stock</option>
        </select>

      </div>

      {/* Inventory Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow-md pb-6">
      <table className="min-w-full table-auto text-sm">
          <thead className="bg-gray-200 text-gray-700">
            <tr>
              <th className="p-4">Item Name</th>
              <th className="p-4">SKU</th>
              <th className="p-4">Category</th>
              <th className="p-4">Quantity</th>
              <th className="p-4">Reorder Level</th>
              <th className="p-4">Unit Price ($)</th>
              <th className="p-4">Total Value ($)</th>
              <th className="p-4">Supplier</th>
              <th className="p-4">Status</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInventory.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50 transition duration-150">
<td className="px-4 py-2 truncate max-w-[150px]">{item.name}</td>
<td className="px-4 py-2 truncate max-w-[100px]">{item.sku}</td>
<td className="px-4 py-2 truncate max-w-[120px]">{item.category}</td>
<td className="px-4 py-2 text-right">{item.quantity}</td>
<td className="px-4 py-2 text-right">{item.reorderLevel}</td>
<td className="px-4 py-2 text-right">${item.unitPrice.toFixed(2)}</td>
<td className="px-4 py-2 text-right">${(item.quantity * item.unitPrice).toFixed(2)}</td>
<td className="px-4 py-2 truncate max-w-[150px]">{item.supplier}</td>
<td className="px-4 py-2 text-center">
  <span
    className={`px-2 py-1 rounded-full text-sm ${
      item.status === "In Stock"
        ? "bg-green-100 text-green-600"
        : item.status === "Low Stock"
        ? "bg-yellow-100 text-yellow-600"
        : "bg-red-100 text-red-600"
    }`}
  >
    {item.status}
  </span>
</td>
<td className="px-2 py-2 flex items-center justify-center space-x-1">
  <button onClick={() => setSelectedItem(item)} className="text-blue-600 hover:text-blue-800 p-1">
    <Eye size={14} />
  </button>
  <button onClick={() => setEditingItem(item)} className="text-green-600 hover:text-green-800 p-1">
    <Edit3 size={14} />
  </button>
  <button onClick={() => handleDeleteItem(item.id)} className="text-red-600 hover:text-red-800 p-1">
    <Trash size={14} />
  </button>
</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default InventoryDashboard;